import { ChevronsUpDown, ClipboardX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecordingPlayer } from './recording-player';
import type { QuestionScoreRow } from './types';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';

interface TranscriptMessage {
  role: string;
  content: string;
  questionId: string | null;
  copyPasteDetected?: boolean;
  createdAt: string;
}

interface TranscriptViewProps {
  messages: TranscriptMessage[];
  questionScores?: QuestionScoreRow[];
  recordingUrl?: string;
}

function MessageBubble({ msg }: { msg: TranscriptMessage }): React.ReactNode {
  const { t } = useTranslation();
  const isFlagged = msg.role !== 'assistant' && msg.copyPasteDetected;
  return (
    <div className={`flex flex-col ${msg.role === 'assistant' ? 'items-start' : 'items-end'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          msg.role === 'assistant'
            ? 'bg-secondary text-secondary-foreground'
            : isFlagged
              ? 'bg-primary text-primary-foreground ring-2 ring-amber-400 ring-offset-1'
              : 'bg-primary text-primary-foreground'
        }`}
      >
        {msg.content}
      </div>
      {isFlagged && (
        <div className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
          <ClipboardX className="size-3" />
          <span>{t('scoring.copyPasteDetected')}</span>
        </div>
      )}
    </div>
  );
}

export function TranscriptView({
  messages,
  questionScores,
  recordingUrl
}: TranscriptViewProps): React.ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const questionTextMap = useMemo(() => {
    const map = new Map<string, string>();
    if (questionScores) {
      for (const qs of questionScores) {
        map.set(qs.questionId, qs.questionText);
      }
    }
    return map;
  }, [questionScores]);

  if (messages.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.transcript')}</h2>
        <CollapsibleTrigger className="inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
          <ChevronsUpDown className="size-4" />
        </CollapsibleTrigger>
      </div>
      {recordingUrl && <RecordingPlayer url={recordingUrl} />}
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          {renderGroupedTranscript(messages, questionTextMap)}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function renderGroupedTranscript(
  messages: TranscriptMessage[],
  questionTextMap: Map<string, string>
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let currentQuestionId: string | null | undefined;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg) continue;

    // When the questionId changes, start a new question group
    if (msg.questionId !== currentQuestionId) {
      currentQuestionId = msg.questionId;

      if (msg.questionId) {
        const questionText = questionTextMap.get(msg.questionId);
        elements.push(
          <div
            key={`q-header-${msg.questionId}-${String(i)}`}
            id={`transcript-q-${msg.questionId}`}
            className="pt-2"
          >
            {questionText && (
              <div className="mb-2 border-b border-border pb-1">
                <p className="text-xs font-medium text-muted-foreground">{questionText}</p>
              </div>
            )}
          </div>
        );
      }
    }

    elements.push(<MessageBubble key={`msg-${String(i)}`} msg={msg} />);
  }

  return elements;
}
